import {
    Component,
    OnDestroy,
    OnInit,
    ViewChildren,
    QueryList,
    AfterViewInit,
    ViewContainerRef,
    ComponentFactoryResolver,
    Injector,
    ComponentRef,
    Output,
    EventEmitter,
} from "@angular/core";
import { Store, Action, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { take, takeUntil, skip, filter } from "rxjs/operators";
import { DependencyList, DependencyName, DependencyDataList } from "./types/DependencyList.interface";
import { WidgetItem } from "./types/WidgetItem.interface";
import { DependencyItem } from "./types/DependencyItem.interface";
import { DependencyObservable } from "./utils/DependencyObservable";
import { trigger, transition, style, animate, keyframes } from "@angular/animations";
import { GridCell } from "./types/GridCell.interface";
import { Subject } from "rxjs";
import importedWidgetList from "./config/widgets-list";
import importedDependencyList from "./config/dependencies-list";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { Contract } from "@edp-components/edp-contract";
import { BannerSize, EdpWidgetBannerComponent } from "@edp-components/edp-card-widgets/edp-widget-banner/edp-widget-banner.component";
import { GetHouseStatusMoonShot } from "@edp-store/actions/define-house.action";

interface AnimationAction extends Action {
    type: string;
    index: number;
    target: HTMLElement;
}

@Component({
    selector: "edp-dashboard-grid",
    templateUrl: "./edp-dashboard-grid.component.html",
    styleUrls: ["./edp-dashboard-grid.component.scss"],
    animations: [
        trigger("cellVisibility", [
            /* Don't remove this line, TS needs it */
        ]),
        trigger("avalancheVisibility", [
            transition(
                "true => false",
                animate(
                    "0.5s cubic-bezier(0.550, 0.055, 0.675, 0.190)",
                    keyframes([
                        style({ transform: "scaleX(1)", "transform-origin": "0 0", offset: 0 }),
                        style({ transform: "scaleX(0)", "transform-origin": "0 0", offset: 1 }),
                    ])
                )
            ),
        ]),
    ],
})
export class EdpDashboardGridComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChildren("widget", { read: ViewContainerRef }) widgetPaleceHolders: QueryList<ViewContainerRef>;
    @Output() isDashboardUnavailable = new EventEmitter();

    /**
     * List of required dependencies
     * Internal list, do not change
     */
    private supraRequiredDependencies: DependencyList = {};

    /**
     * Internal List, please don't change
     */
    private widgetsByName: { [key: string]: WidgetItem } = {};

    /**
     * Internal List, please don't change
     */
    public gridCells: GridCell[] = [];

    /**
     * Resolves after all required dependencies are met
     * Internal, please don't change
     */
    private dependenciesPromise: Promise<any>;

    /**
     * Observable for animations
     */
    private animationsObservable = new Subject<AnimationAction>();

    /**
     * If you have a new dependency, just declare it here
     * (it must be in the store and have a selector)
     */
    private dependencies: DependencyList = importedDependencyList;

    /**
     * Change this list if you need to
     * add or remove widgets in the dashboard
     */
    public widgets: WidgetItem[] = [];

    /**
     * Widget counters
     */
    public widgetCounters = {
        A: {
            max: 9,
            counter: 0,
        },
        B: {
            max: 2,
            counter: 0,
        },
        C: {
            max: 2,
            counter: 0,
        },
        D: {
            max: 1,
            counter: 0,
        },
    };

    private unsubscribe = new Subject();

    public constructor(
        private store: Store<IAppState>,
        protected componentFactoryResolver: ComponentFactoryResolver,
        protected injector: Injector
    ) {}

    /**
     * Bootstrap method
     */
    public ngOnInit(reset = false): void {
        if (reset) {
            this.ngOnDestroy(true);
        }

        // in case you wondering, this.widgets = [...importedWidgetList] doesn't work
        let i = 0;
        for (const widget of importedWidgetList) {
            this.widgets[i] = { ...widget };
            i++;
        }

        // int dependencies
        this.dependenciesPromise = this.initDependencies();

        // init widgets
        this.initWidgets();

        this.dependenciesPromise
            // fallback, inform user about this
            .catch((error: Error) => {
                this.isDashboardUnavailable.emit(true);
                console.log("Oops! Something went wrong :(");
            });

        if (reset) {
            this.ngAfterViewInit();
            DependencyObservable.resumeAll();
        }

        if (!reset) {
            this.isDashboardUnavailable.emit(false);
            this.store
                .pipe(
                    takeUntil(this.unsubscribe),
                    select(getContract),
                    filter((val: Contract) => val !== null && val !== undefined),
                    skip(1) // skip first value to prevent double initiation
                )
                .subscribe(() => {
                    // to not loose any notification in case a
                    // service responce is super fast (can happen when aliens dominate earth)
                    DependencyObservable.pauseAll();
                    this.ngOnInit(true);
                });
        }

        this.store.dispatch(new GetHouseStatusMoonShot());
    }

    /**
     * Bootstrap widgets
     */
    public ngAfterViewInit() {
        // beacause when this hook is called,
        // view is actually not initiated
        setTimeout(() => {
            this.dependenciesPromise
                // init widgets
                .then(this.loadWidgets.bind(this));
        }, 1);
    }

    public onAnimationEvent(evt: any, index: number, stage: "START" | "END", type: "GRID" | "WIDGET", displayBlock: boolean) {
        if (displayBlock) {
            evt.element.classList.remove("d-none");
        } else {
            evt.element.classList.add("d-none");
        }

        this.animationsObservable.next({
            type: type + "_ANIM_" + stage + "_" + index,
            index,
            target: evt.element,
        });
    }

    private callSlotsInWidgetsByName(widgetName: string): number {
        const widget = this.widgetsByName[widgetName];
        return widget.getSlotsCount.call(widget, this.getWidgetDependenciesData(widget));
    }

    /**
     * @returns Return availables widgets and sorted by position
     */
    private getAvailablesWidgets(): Array<{ name: string; slots: number; position: number }> {
        return Object.keys(this.widgetsByName)
            .filter(i => this.callSlotsInWidgetsByName(i) > 0)
            .map(i => {
                return {
                    name: i,
                    slots: this.callSlotsInWidgetsByName(i),
                    position: this.widgetsByName[i].position,
                };
            })
            .sort((a, b) => a.position - b.position);
    }

    /**
     * @returns consider double widgets and check if it is breaking layout widgets
     */
    private calculateDoubleWidgetsPositions(): void {
        const availables = this.getAvailablesWidgets();
        const breakNumber = 3;
        let countSlots = 0;
        for (const index of Object.keys(availables)) {
            const availableWidget = availables[index];
            countSlots += availableWidget.slots;
            if (countSlots > breakNumber && countSlots % breakNumber !== 0) {
                const currentWidgetPosition = availableWidget.position;
                const newWidgetPosition = availables[Number(index) - 1].position;
                this.moveWidgetInGrid(currentWidgetPosition, newWidgetPosition);
                break;
            }
            if (countSlots === breakNumber) {
                countSlots = 0;
            }
        }
    }

    /**
     * This function is responsable for ordering widgets in the dashboard
     * which in turn, if the order changed, should relegate the DOM changes
     * to another function.
     *
     * @returns True if any widget position has changed or false otherwise
     */
    private calculateWidgetPositions(): boolean {
        this.resetWidgetCounters();

        // calculate consumptions widget position
        let somethingChanged = false;
        let currentWidgetPosition = this.widgetsByName.contractConsumptions.position;
        let newWidgetPosition = currentWidgetPosition;
        if (this.dependencies.balanceData.data.debtBalance > 0) {
            newWidgetPosition = 1;
        } else if (
            this.dependencies.balanceData.data.debtBalance === 0 &&
            this.dependencies.contract.data.HasContaCerta &&
            (this.dependencies.balanceData.data.balance === 0 || this.dependencies.contract.data.HasDirectDebit)
        ) {
            newWidgetPosition = 5;
        } else {
            newWidgetPosition = 4;
        }

        if (newWidgetPosition !== currentWidgetPosition) {
            // tslint:disable-next-line: no-unused-expression
            somethingChanged || this.displayAvalancheLoader();
            this.moveWidgetInGrid(currentWidgetPosition, newWidgetPosition);
            somethingChanged = true;
        }

        // calculating type B widgets sequence
        if (this.dependencies.contract.data.HasPack) {
            // with pack
            currentWidgetPosition = newWidgetPosition = this.widgetsByName.savings.position;
            // if (
            //     !this.evaluateWidgetPresence(this.widgetsByName.partners, false) &&
            //     !this.evaluateWidgetPresence(this.widgetsByName.advantagesAndBenefits, false)
            // ) {
            //     // move savings to 7 (before funciona and after advantagesAndBenefits)
            //     newWidgetPosition = this.widgetsByName.advantagesAndBenefits.position + 1;
            // }
        } else {
            // without pack
            // moving health to X (after funciona and before savings)
            currentWidgetPosition = this.widgetsByName.health.position;
            newWidgetPosition = this.widgetsByName.funciona.position + 1;
        }

        if (newWidgetPosition !== currentWidgetPosition) {
            // tslint:disable-next-line: no-unused-expression
            somethingChanged || this.displayAvalancheLoader();
            this.moveWidgetInGrid(currentWidgetPosition, newWidgetPosition);
            somethingChanged = true;
        }

        // calculating type D widgets sequence and consider double widgets to don't break layout widgets
        if (!!this.dependencies.onboardingWhatsNew.data) {
            this.calculateDoubleWidgetsPositions();
        }

        return somethingChanged;
    }

    /**
     * Move a widget in the grid
     *
     * @param from From prosition
     * @param to To position
     */
    private moveWidgetInGrid(from: number, to: number): void {
        if (from === to || from < 0 || to < 0) {
            return;
        }
        const direction = from > to ? -1 : from === to ? 0 : 1;

        const movedItem = this.gridCells[from].widget;
        let i = from;
        this.removeWidgetFromDOM(this.gridCells[i].widget, false);
        do {
            i += direction;

            this.removeWidgetFromDOM(this.gridCells[i].widget, false);

            this.gridCells[i - direction].widget = this.gridCells[i].widget;
            this.gridCells[i - direction].widget.position = i - direction;

            this.evaluateWidgetPresence(this.gridCells[i - direction].widget, false);

            if (i === to) {
                break;
            }
        } while (1);

        this.gridCells[i].widget = movedItem;
        this.gridCells[i].widget.position = i;

        this.evaluateWidgetPresence(this.gridCells[i].widget, false);
    }

    /**
     * Just initialize the observables for each declared
     * dependency.
     * In case the dependency is required on first load
     * (in order to decide whidget sequence)
     * a promise is created for that dependency.
     *
     * @returns Allways returns a promise that reolves as soons as
     * all required dependencies are met
     */
    private initDependencies(): Promise<any> {
        const requiredDependencies: Promise<any>[] = [];
        let dependency: DependencyItem<any>;

        // for each declared dependency, create an observable
        for (const depName of Object.keys(this.dependencies)) {
            dependency = this.dependencies[depName];
            if (!dependency.observable) {
                // create the observable that will update the dependency data
                // all widgets that have this dependency will subscribe to this obervable
                // and act accordingly (see initWidgets() method)
                dependency.observable = DependencyObservable.create<any>(this.store, dependency);
            }

            if (dependency.requiredOnInit) {
                // this is a required dependency: start fetching data
                dependency.initPromise = dependency.observable.pipe(take(1)).toPromise();
                requiredDependencies.push(dependency.initPromise);

                this.supraRequiredDependencies[depName] = dependency;
            }
        }

        if (requiredDependencies.length) {
            // return a promise that will resolve as soon
            // as all required dependencies are met
            return Promise.all(requiredDependencies);
        } else {
            // there are no required dependencies,
            // just return a resolved promise
            return Promise.resolve();
        }
    }

    /**
     * Initialize all declared widgets
     */
    private initWidgets(): void {
        let widget: WidgetItem;
        let i = 0;
        for (widget of this.widgets) {
            widget.visible = false;
            widget.disabled = typeof widget.disabled === "undefined" ? false : widget.disabled;
            widget.position = -1;

            if (widget.disabled) {
                // if widget is disabled, skip it
                continue;
            }

            widget.position = i;

            // add to the grid
            this.gridCells[i] = {
                widget,
                visible: true,
            };

            this.widgetsByName[widget.name] = widget;
            i++;
        }
    }

    /**
     * Start resolving widgets dependencies and load
     * the widget as soon as all dependencies are met
     */
    private loadWidgets(): void {
        let widget: WidgetItem;
        const allWidgetPromises: Promise<any>[] = [];
        for (const gCell of this.gridCells) {
            widget = gCell.widget;
            if (widget.dependencies.length > 0) {
                allWidgetPromises.push(
                    this.getPromiseFromWidgetDependencies(widget.dependencies).catch(
                        ((widget: WidgetItem, error: Error) => {
                            // fail silently to the console (for debug purposes),
                            // the user will keep seeing the avalanche loader forever
                            this.isDashboardUnavailable.emit(true);
                            console.error("Failed to load widget " + widget.name + " dependencies with error", error);
                            return error;
                        }).bind(this, widget)
                    )
                );
            } else {
                this.getPromiseFromWidgetDependencies(widget.dependencies)
                    // Note: we must use bind or the same widget reference (the last) is used in all callbacks
                    .then(this.evaluateWidgetPresence.bind(this, widget))
                    .catch(
                        ((widget: WidgetItem, error: Error) => {
                            this.isDashboardUnavailable.emit(true);
                            // fail silently to the console (for debug purposes),
                            // the user will keep seeing the avalanche loader forever
                            console.error("Failed to load widget " + widget.name + " dependencies with error", error);
                        }).bind(this, widget)
                    );
            }
        }

        Promise.all(allWidgetPromises)
            // calculate widget positions
            .then(this.calculateWidgetPositions.bind(this))
            .then(this.evaluateAllWidgets.bind(this))
            .catch(error => {
                this.isDashboardUnavailable.emit(true);
                // fail silently to the console (for debug purposes),
                // the user will keep seeing the avalanche loader forever
                console.error("Error loading widgets :(");
            });
    }

    /**
     * Displays avalanche loader in all cells (if they not have dependencies)
     */
    private displayAvalancheLoader(includeHidden = true) {
        for (const cell of this.gridCells) {
            if (cell.widget.visible && cell.widget.dependencies.length > 0) {
                cell.widget.visible = false;
            }
            if (includeHidden && !cell.visible) {
                cell.visible = true;
            }
        }
    }

    /**
     * Evaluate all widgets presence
     */
    private evaluateAllWidgets() {
        this.resetWidgetCounters();
        for (const i in this.gridCells) {
            if (!this.gridCells[i]) {
                continue;
            }

            this.evaluateWidgetPresence.call(this, this.gridCells[i].widget);
        }

        this.completeTheGrid(this.widgetCounters.A.counter + this.widgetCounters.B.counter + this.widgetCounters.D.counter);
    }

    /**
     * This function calls the widget evaluate function and takes action accordingly
     * to the evaluation result
     *
     * @param widget           Target widget
     * @param hideLoaderOrCell Optional bollean indicating whether this function should
     * hide the loader or hide the cell
     * @returns True if the widget should be visible, false otherwise
     */
    private evaluateWidgetPresence(widget: WidgetItem, hideLoaderOrCell = true): boolean {
        const shouldDisplayWidget: boolean =
            this.isWidgetBelowMax(widget) && widget.evaluateVisibility.call(widget, this.getWidgetDependenciesData(widget));

        if (hideLoaderOrCell && shouldDisplayWidget && (!widget.visible || !this.gridCells[widget.position].visible)) {
            // add widget to DOM
            this.addWidgetToDOM(widget, hideLoaderOrCell);
        } else if (!shouldDisplayWidget && (widget.visible || this.gridCells[widget.position].visible)) {
            // remove widget from DOM
            this.removeWidgetFromDOM(widget, hideLoaderOrCell);
        }

        if (hideLoaderOrCell && shouldDisplayWidget) {
            this.widgetCounters[widget.category].counter += widget.getSlotsCount.call(widget, this.getWidgetDependenciesData(widget));
        }

        return shouldDisplayWidget;
    }

    /**
     * For a given widget avaliates if it can be added to the
     * dashboard or not taking into account only the widget
     * category limit
     *
     * @param widget The widget that you need to validate
     * @returns True if the widget can be added, false otherwise
     */
    public isWidgetBelowMax(widget: WidgetItem): boolean {
        return this.widgetCounters[widget.category].counter < this.widgetCounters[widget.category].max;
    }

    /**
     * Reset widgets counters (in all categories)
     */
    private resetWidgetCounters(): void {
        for (const category of Object.keys(this.widgetCounters)) {
            this.widgetCounters[category].counter = 0;
        }
    }

    /**
     * Taking widget dependencies names array, returns a promise
     * that will resolve as soon as all dependencies are met
     *
     * @param dependencies Dependencies list
     * @param skipList List of dependency named to exclude from the promise
     * @returns A promise that will resolve as soon as all widget dependencies are met
     */
    private getPromiseFromWidgetDependencies(dependencies: DependencyName[], skipList: DependencyName[] = []): Promise<any> {
        const promiseList: Promise<any>[] = [];

        for (const depName of dependencies) {
            if (skipList.includes(depName)) {
                continue;
            }

            if (!this.dependencies[depName].initPromise) {
                this.dependencies[depName].initPromise = this.dependencies[depName as string].observable.pipe(take(1)).toPromise();
            }

            promiseList.push(this.dependencies[depName].initPromise);
        }

        if (promiseList.length) {
            // return a promise that will resolve as soon
            // as all required dependencies are met
            return Promise.all(promiseList);
        } else {
            // there are no dependencies,
            // just return a resolved promise
            return Promise.resolve();
        }
    }

    /**
     * Get the list of dependency values related to a given widget
     *
     * @param widget Given widget
     * @returns the dependency list
     */
    private getWidgetDependenciesData(widget: WidgetItem): DependencyDataList {
        const dataParam = {};
        for (const depName of widget.dependencies) {
            dataParam[depName] = this.dependencies[depName].data;
        }

        return dataParam;
    }

    /**
     * Get the total number of occupied grid slots
     *
     * @returns the number of occupied grid slots
     */
    private getNumberOfOccupiedSlots(): number {
        let counter = 0;
        for (const widget of this.widgets) {
            if (widget.name === "banner") {
                // don't count with banner widget
                continue;
            }

            counter += widget.getSlotsCount.call(widget, this.getWidgetDependenciesData(widget));
        }

        return counter;
    }

    /**
     * Complete grid empty spaces (at the end)
     *
     * @param occupied Number of occupied slots in the grid, if not supplyed, a new countae is made
     * @returns Boolean true if the banner widget was added, false if no chnage is made
     */
    private completeTheGrid(occupied?: number): boolean {
        occupied = occupied || this.getNumberOfOccupiedSlots();
        if (occupied > 6 && occupied % 3 === 0) {
            this.widgetsByName.banner.evaluateVisibility = () => false;
            return false;
        }

        const freeSlots = 3 - (occupied % 3);
        let size: BannerSize;
        switch (freeSlots) {
            case 1:
                size = BannerSize.SMALL;
                break;
            case 2:
                size = BannerSize.MEDIUM;
                break;
            case 3:
                size = occupied === 3 ? BannerSize.LARGE : BannerSize.LARGER;
                break;
        }

        this.widgetsByName.banner.evaluateVisibility = () => true;
        this.evaluateWidgetPresence(this.widgetsByName.banner);
        (this.widgetsByName.banner.componentInstance as ComponentRef<EdpWidgetBannerComponent>).instance.size = size;

        return true;
    }

    /**
     * Inject widget in the DOM
     *
     * @param widget Target widget to inject
     * @param hideAvalanche Whether this function should hide avalanche at
     * the end. Default is true
     */
    private addWidgetToDOM(widget: WidgetItem, hideAvalanche = true): void {
        // if the cell is not visible, make it visible (if
        // things are done properly, it should be already visible at this point)
        if (!this.gridCells[widget.position].visible) {
            // make sure the widget will be hidden by the avalanche
            widget.visible = false;

            // forcing avalanche to be displayed
            this.gridCells[widget.position].visible = true;
        }

        if (!widget.visible) {
            if (!widget.componentInstance) {
                // Creating an instance of the component
                widget.componentInstance = this.componentFactoryResolver.resolveComponentFactory(widget.component).create(this.injector);
            }

            // add to DOM
            const cellsArr = this.widgetPaleceHolders.toArray();
            if (!cellsArr[widget.position].length) {
                cellsArr[widget.position].insert(widget.componentInstance.hostView);
            }

            if (hideAvalanche) {
                // make the widget visible
                widget.visible = true;
            }
        }
    }

    /**
     * Remove widget from DOM
     *
     * @param widget Target widget to remove
     * @param hideCell Whether this function should hide the widget cell at the end. Default is true.
     */
    private removeWidgetFromDOM(widget: WidgetItem, hideCell = true): void {
        // if grid is not visible, then force widget visibility to hidden
        if (!this.gridCells[widget.position].visible) {
            widget.visible = false;
        }

        // if widget is visible, force avalanche to be displayed
        if (this.gridCells[widget.position].visible && widget.visible) {
            // forcing avalanche to be displayed (and come back later when the animation ends)
            widget.visible = false;
        }

        const cellsArr = this.widgetPaleceHolders.toArray();
        if (cellsArr[widget.position].length) {
            // remove from DOM
            cellsArr[widget.position].detach(0);
        }

        if (hideCell) {
            this.gridCells[widget.position].visible = false;
        }
    }

    public ngOnDestroy(softDestroy = false): void {
        if (!softDestroy) {
            this.unsubscribe.next();
            this.unsubscribe.complete();

            // for each declared dependency, stop subscriptions
            DependencyObservable.destroyAll();
        }

        for (const widget of this.widgets) {
            if (widget.componentInstance) {
                widget.componentInstance.hostView.destroy();
                widget.componentInstance.destroy();
                widget.componentInstance = null;
            }
        }

        for (const depName of Object.keys(this.dependencies)) {
            this.dependencies[depName].data = null;
            this.dependencies[depName].initPromise = null;
        }

        this.widgets = [];
        this.gridCells = [];
        this.supraRequiredDependencies = {};
        this.widgetsByName = {};

        if (!softDestroy) {
            this.animationsObservable.next();
            this.animationsObservable.complete();
            this.animationsObservable = null;
        }
    }
}
